/**
 * Küçük, ad-alanı duyarsız XML okuyucu.
 *
 * Neden hazır bir kütüphane değil: bu paket SAF kalmak zorunda — DOM yok, ağ
 * yok. Tarayıcıda `DOMParser` var ama Worker'da ve testte yok; bir kütüphane
 * eklemek ise 150 satırlık bir iş için yeni bir bağımlılık demek.
 *
 * Neden ad alanları SÖKÜLÜYOR: e-fatura formatları aynı alanı bambaşka
 * öneklerle yazar — `cbc:ID`, `ram:ID`, `ns2:ID` hepsi aynı şeydir. Öneki
 * atıp yalnızca yerel ada bakmak, tek bir okuyucunun UBL'i de CII'yi de
 * anlamasını sağlar.
 *
 * KAPSAM: e-fatura belgelerini okumaya yeter. Genel amaçlı bir XML
 * ayrıştırıcı DEĞİLDİR — DTD, varlık tanımı ve karışık içerik desteklemez.
 * Anlamadığı yapıda sessizce yanlış cevap vermek yerine boş döner.
 */

export interface XmlNode {
  /** Ad alanı öneki sökülmüş etiket adı (`cbc:ID` → `ID`). */
  readonly name: string;
  readonly attrs: Readonly<Record<string, string>>;
  readonly children: readonly XmlNode[];
  /** Yalnızca doğrudan metin içeriği (alt düğümlerinki değil). */
  readonly text: string;
}

const EMPTY: XmlNode = { name: '', attrs: {}, children: [], text: '' };

/** Ağacın izin verilen en fazla derinliği. Gerçek e-faturalar bunun çok
 *  altında kalır; amaç yığın-taşması DoS'unu kapatmak. */
const MAX_DEPTH = 256;

function stripNs(tag: string): string {
  const i = tag.indexOf(':');
  return i === -1 ? tag : tag.slice(i + 1);
}

function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    /* &amp; EN SONA çözülür. Önce çözülseydi "&amp;lt;" iki adımda "<" olurdu
       ve belgede gerçekten yazan "&lt;" metni kaybolurdu. */
    .replace(/&amp;/g, '&');
}

function parseAttrs(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([A-Za-z_][\w.:-]*)\s*=\s*"([^"]*)"|([A-Za-z_][\w.:-]*)\s*=\s*'([^']*)'/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const key = stripNs(m[1] ?? m[3] ?? '');
    const val = m[2] ?? m[4] ?? '';
    if (key) out[key] = decodeEntities(val);
  }
  return out;
}

interface Mutable {
  name: string;
  attrs: Record<string, string>;
  children: Mutable[];
  text: string;
}

/**
 * XML metnini ağaca çevir. Ayrıştırılamazsa boş düğüm döner — istisna atmaz,
 * çünkü çağıran zaten "bu bir e-fatura mı" diye soruyor ve "hayır" normal
 * bir cevaptır.
 */
export function parseXml(source: string): XmlNode {
  if (!source || typeof source !== 'string') return EMPTY;

  /* CDATA yer tutucuyla DEĞİL, kaçış dizisine çevrilerek korunur: içeriği
     sıradan metne dönüşür ve aşağıdaki normal metin yolu onu geri çözer.
     Yer tutucu kullanmak, belgenin kendi metniyle çakışma riski taşıyordu. */
  const s = source
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, (_, body: string) =>
      body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\?[\s\S]*?\?>/g, '')
    .replace(/<!DOCTYPE[^>]*>/gi, '');

  const root: Mutable = { name: '#root', attrs: {}, children: [], text: '' };
  const stack: Mutable[] = [root];

  const tagRe = /<\s*(\/?)\s*([A-Za-z_][\w.:-]*)((?:"[^"]*"|'[^']*'|[^>])*?)(\/?)\s*>/g;
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = tagRe.exec(s)) !== null) {
    const full = m[0];
    const closing = m[1];
    const rawName = m[2];
    const rawAttrs = m[3];
    const selfClose = m[4];

    const between = s.slice(last, m.index);
    if (between.trim()) {
      const top = stack[stack.length - 1];
      top.text += decodeEntities(between).trim();
    }
    last = m.index + full.length;

    const name = stripNs(rawName);

    if (closing) {
      /* Eşleşmeyen kapanış etiketi yok sayılır: bozuk bir belgede yığını
         bozmaktansa o etiketi görmezden gelmek daha güvenli. */
      for (let i = stack.length - 1; i > 0; i--) {
        if (stack[i].name === name) {
          stack.length = i;
          break;
        }
      }
      continue;
    }

    const node: Mutable = { name, attrs: parseAttrs(rawAttrs), children: [], text: '' };
    stack[stack.length - 1].children.push(node);
    /* Derinlik SINIRLI. Kötü niyetli bir belge on binlerce kez iç içe etiket
       göndererek ağacı çok derinleştirebilir; sonra ağaçta gezen kod yığını
       taşırıp kurbanın sekmesini çökertirdi (bir muhasebeci müşterisinin
       hazırladığı faturayı açtığında). Gerçek bir e-fatura en fazla birkaç
       düzine seviye derindir; sınırın ötesinde artık iç içe girilmez, düğüm
       o seviyede kalır — içerik kaybolmaz, yalnızca patolojik derinlik durur. */
    if (!selfClose && stack.length < MAX_DEPTH) stack.push(node);
  }

  return root.children.length === 1 ? (root.children[0] as XmlNode) : (root as XmlNode);
}

/* ── Ağaçta gezinme ────────────────────────────────────────────────── */

/** Doğrudan çocuklar arasında bu ada sahip olanlar. */
export function childrenNamed(node: XmlNode | null, name: string): XmlNode[] {
  if (!node) return [];
  return node.children.filter((c) => c.name === name);
}

/**
 * Doğrudan çocuklar arasında bu ada sahip ilk düğüm.
 *
 * `null` girdi kabul eder ve `null` döner — `path`/`text` ile aynı davranış.
 * Böylece zincirleme erişimlerde her adımda null kontrolü yazılmaz.
 */
export function child(node: XmlNode | null, name: string): XmlNode | null {
  if (!node) return null;
  return node.children.find((c) => c.name === name) ?? null;
}

/**
 * Yol izle: `path(root, 'LegalMonetaryTotal', 'PayableAmount')`.
 * Yol kopuksa null — her adımda null kontrolü yazmaktan kurtarır.
 */
export function path(node: XmlNode | null, ...names: readonly string[]): XmlNode | null {
  let cur: XmlNode | null = node;
  for (const n of names) {
    if (!cur) return null;
    cur = child(cur, n);
  }
  return cur;
}

/** Yolun sonundaki metin. Yol kopuksa boş dize. */
export function text(node: XmlNode | null, ...names: readonly string[]): string {
  return path(node, ...names)?.text ?? '';
}

/**
 * Herhangi bir derinlikte bu ada sahip ilk düğüm.
 *
 * Sarmalayıcı katmanlar formatlar arasında değişiyor (CII'de
 * `SupplyChainTradeTransaction` var, UBL'de yok); tam yol yazmak yerine
 * alanı derinlemesine aramak okuyucuyu iki formata da dayanıklı yapar.
 */
export function findFirst(node: XmlNode | null, name: string): XmlNode | null {
  if (!node) return null;
  /* ÖZYİNELEMESİZ (açık yığın). Özyinelemeli hâli, derin iç içe bir belgede
     çağrı yığınını taşırıp sekmeyi çökertiyordu. parseXml artık derinliği
     zaten sınırlıyor; bu, ağaç başka bir yoldan derin gelse bile güvenli
     kalmak için ikinci savunma. */
  const stack: XmlNode[] = [node];
  while (stack.length) {
    const n = stack.pop() as XmlNode;
    if (n.name === name) return n;
    /* İlk-eşleşme sırasını (belge sırası) korumak için çocukları ters ekle. */
    for (let i = n.children.length - 1; i >= 0; i--) stack.push(n.children[i]);
  }
  return null;
}

/** Herhangi bir derinlikte bu ada sahip tüm düğümler. Özyinelemesiz. */
export function findAll(node: XmlNode | null, name: string): XmlNode[] {
  const out: XmlNode[] = [];
  if (!node) return out;
  const stack: XmlNode[] = [node];
  while (stack.length) {
    const n = stack.pop() as XmlNode;
    if (n.name === name) out.push(n);
    for (let i = n.children.length - 1; i >= 0; i--) stack.push(n.children[i]);
  }
  return out;
}
